import { PaginatedAPIResponse, APIResponse } from "../../../models/NetworkResponse";
import { Employee, Resetpassword } from "../../../models/Employees/Employee";
import { deleteAPI, getAPI, postAPI, putAPI } from "../../../api/Networking";
import { GetEmployeeParams } from "../../../models/Employees/GetEmployeeParams";
import { PermissionProject } from "../../../models/Employees/PermissionProject";

function optionalDiscountPercentage(value: unknown): number | null {
  if (value === undefined || value === null) return null;
  const text = String(value).trim();
  if (text === "") return null;
  const parsed = Number(text);
  if (!Number.isFinite(parsed)) {
    throw new Error("Discount must be a number.");
  }
  return parsed;
}

function withOptionalDiscount<T extends { discount_percentage?: unknown }>(
  employee: T,
): T & { discount_percentage: number | null } {
  return {
    ...employee,
    discount_percentage: optionalDiscountPercentage(employee.discount_percentage),
  };
}

export default class EmployeeAPI {
  fetchEmployee = async (
    params: GetEmployeeParams
  ): Promise<PaginatedAPIResponse<Employee>> => {
    try {
      const queryParams = new URLSearchParams();
      if (params.page_no)
        queryParams.append("page_no", params.page_no.toString());
      if (params.limit) queryParams.append("limit", params.limit.toString());
      if (params.sortBy) queryParams.append("sort_by", params.sortBy);
      if (params.sortType)
        queryParams.append("sort_type", params.sortType.toString());
      if (params.search_text)
        queryParams.append("search_text", params.search_text);
      if (params.team_id) queryParams.append("team_id", params.team_id.id);
      if (params.active && params.active.length > 0)
        queryParams.append(
          "active",
          params.active.map((v) => (v === 1 ? "true" : "false")).join(","),
        );
      const response = await getAPI(`/v5/admins?${queryParams.toString()}`);
      return response.result as PaginatedAPIResponse<Employee>;
    } catch (error) {
      throw error;
    }
  };

  searchEmployees = async (
    params: GetEmployeeParams,
  ): Promise<PaginatedAPIResponse<Employee>> => {
    try {
      const queryParams = new URLSearchParams();
      if (params.page_no) {
        queryParams.append("page_no", params.page_no.toString());
      }
      if (params.limit) queryParams.append("limit", params.limit.toString());
      if (params.sortBy) queryParams.append("sort_by", params.sortBy);
      if (params.sortType) {
        queryParams.append("sort_type", params.sortType.toString());
      }
      if (params.search_text) {
        queryParams.append("search_text", params.search_text);
      }
      if (params.active && params.active.length > 0) {
        queryParams.append(
          "active",
          params.active.map((value) => (value === 1 ? "true" : "false")).join(","),
        );
      }
      const response = await getAPI(
        `/v1/employees/search?${queryParams.toString()}`,
      );
      return response.result as PaginatedAPIResponse<Employee>;
    } catch (error) {
      throw error;
    }
  };

  getEmployee = async (
    params: GetEmployeeParams
  ): Promise<PaginatedAPIResponse<Employee>> => {
    try {
      const queryParams = new URLSearchParams();
      if (params.page_no)
        queryParams.append("page_no", params.page_no.toString());
      if (params.limit) queryParams.append("limit", params.limit.toString());
      if (params.sortBy) queryParams.append("sort_by", params.sortBy);
      if (params.sortType)
        queryParams.append("sort_type", params.sortType.toString());
      if (params.search_text)
        queryParams.append("search_text", params.search_text);
      if (params.active && params.active.length > 0)
        queryParams.append(
          "active",
          params.active.map((v) => (v === 1 ? "true" : "false")).join(","),
        );
      const response = await getAPI(
        `/v5/admins/view?${queryParams.toString()}`
      );
      return response.result as PaginatedAPIResponse<Employee>;
    } catch (error) {
      throw error;
    }
  };

  editEmployee = async (
    employeeDetails?: Employee & { project?: PermissionProject },
  ): Promise<APIResponse> => {
    try {
      const response = await putAPI(
        `/v1/employees/${employeeDetails?.id}`,
        employeeDetails ? withOptionalDiscount(employeeDetails) : employeeDetails,
      );
      return response;
    } catch (error) {
      throw error;
    }
  };

  getEditedEmployees = async (): Promise<APIResponse> => {
    try {
      const response = await getAPI(`/v5/admins/admin/details`);
      return response;
    } catch (error) {
      throw error;
    }
  };

  getAdminDetails = async (
    adminId: number,
    project: PermissionProject = "swageazy",
  ): Promise<APIResponse> => {
    try {
      const response = await getAPI(
        `/v1/employees/${adminId}?project=${project}`,
      );
      return response;
    } catch (error) {
      throw error;
    }
  };

  teamPermission = async (
    adminId: number,
    project: PermissionProject = "swageazy",
  ): Promise<APIResponse> => {
    try {
      const response = await getAPI(
        `/v5/admins/admin/admin-id/${adminId}/team/permissions?project=${project}`
      );
      return response;
    } catch (error) {
      throw error;
    }
  };

  fetchEligibleTeams = async (
    project: PermissionProject = "swageazy",
  ): Promise<APIResponse> => {
    try {
      const response = await getAPI(
        `/v1/employees/teams?project=${project}`,
      );
      return response;
    } catch (error) {
      throw error;
    }
  };

  fetchTeamPermissions = async (
    teamIds: number[],
    project: PermissionProject = "swageazy",
  ): Promise<APIResponse> => {
    try {
      const ids = teamIds.filter((teamId) => teamId > 0).join(",");
      const response = await getAPI(
        `/v1/employees/teams/permissions?project=${project}&team_ids=${ids}`,
      );
      return response;
    } catch (error) {
      throw error;
    }
  };

  getAllTeam = async (): Promise<APIResponse> => {
    try {
      const response = await getAPI(`/v5/admins/teams`);
      return response;
    } catch (error) {
      throw error;
    }
  };

  addEmployee = async (
    employeeDetails: Employee & { project?: PermissionProject },
  ): Promise<APIResponse> => {
    try {
      const response = await postAPI(
        `/v1/employees`,
        withOptionalDiscount(employeeDetails),
      );
      return response;
    } catch (error) {
      throw error;
    }
  };

  resetPassword = async (email: Resetpassword): Promise<APIResponse> => {
    try {
      const response = await postAPI(`/v1/employees/reset-password`, {
        email: email,
      });
      return response;
    } catch (error) {
      throw error;
    }
  };

  deleteEmployee = async (employeeId: number): Promise<APIResponse> => {
    try {
      const response = await deleteAPI(
        `/v5/admins/admin/admin-id/${employeeId}`
      );
      return response;
    } catch (error) {
      throw error;
    }
  };
}
